import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError } from "@/hooks";
import { api } from "@/lib/api";
import { type Notification, type Paginate, type SearchRequest } from "@/types";
import { getNextPaginatePage } from "@/utils";

export type NotificationList = Paginate<Notification>;

export const notifications = createQueryKeys("notifications", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  allInfinite: (filter?: SearchRequest) => [{ filter }],
  one: (id?: string) => [id],
});

const include = "";

export const fetchNotificationsQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...notifications.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<NotificationList>(`/me/notifications`, {
          params: { page: pageParam, include, ...search },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchNotifications = (search?: SearchRequest) =>
  useInfiniteQuery({
    ...fetchNotificationsQueryOptions(search),
  });

export const fetchNotificationQueryOptions = (id?: string) =>
  queryOptions({
    ...notifications.one(id),
    queryFn: () =>
      api
        .get<Notification>(`/me/notifications/${id}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchNotification = (id?: string) =>
  useQuery(fetchNotificationQueryOptions(id));

export const useReadNotification = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (id: string) =>
      api.put(`/me/notifications/${id}/read`).then((response) => response.data),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries(notifications.one(id));
      return queryClient.invalidateQueries({
        queryKey: notifications.all._def,
      });
    },
  });
};

export const useReadNotifications = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: () => api.put(`/me/notifications/read-all`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notifications.allInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: notifications.all._def,
      });
    },
  });
};
